import { workOrderService } from './workOrderService'
import { workOrderOfflineStorage } from './offlineStorage'
import { workOrderPrefetchService } from './workOrderPrefetchService'
import type { WorkOrder, TechnicianDayDestination } from './workOrderService'

export interface WorkOrdersListState {
  exists: boolean
  isOffline: boolean
  lastModified?: number
  lastSyncTime?: number
  count: number
}

class OfflineWorkOrdersListService {
  
  /**
   * Load work orders with offline-first strategy for today's date only
   * Priority: Server > Offline cache (today only) > Empty array
   */
  async loadWorkOrdersList(technicianId: number, date: string, statusFilter: string): Promise<{
    data: WorkOrder[]
    state: WorkOrdersListState
  }> {
    
    const isToday = date === new Date().toISOString().split('T')[0]
    
    // 1. If online, try to fetch from server first
    if (navigator.onLine) {
      try {
        const serverWorkOrders = await workOrderService.getFilteredWorkOrders(
          technicianId,
          date,
          statusFilter
        )
        
        // Update offline cache with server data
        // await this.updateOfflineCache(serverWorkOrders)
        
        // Trigger prefetch for today's work orders in background
        if (isToday) {
          workOrderPrefetchService.prefetchTodayWorkOrders(technicianId).catch(error => {
          })
        }
        
        return {
          data: serverWorkOrders,
          state: {
            exists: serverWorkOrders.length > 0,
            isOffline: false,
            lastModified: Date.now(),
            lastSyncTime: Date.now(),
            count: serverWorkOrders.length
          }
        }
      } catch (error) {
        // Continue to offline fallback only if it's today's date
      }
    }
    
    // 2. Only use offline data if it's today's date
    if (isToday) {
      const offlineWorkOrders = await workOrderOfflineStorage.getFilteredWorkOrdersOffline(
        technicianId, 
        date, 
        statusFilter
      )
      
      if (offlineWorkOrders.length > 0) {
        return {
          data: offlineWorkOrders,
          state: {
            exists: true,
            isOffline: true,
            lastModified: Date.now(),
            count: offlineWorkOrders.length
          }
        }
      }
    }
    
    // 3. No data available
    return {
      data: [],
      state: {
        exists: false,
        isOffline: !navigator.onLine,
        lastModified: Date.now(),
        count: 0
      }
    }
  }
  
  /**
   * Update offline cache with server data
   */
  private async updateOfflineCache(workOrders: WorkOrder[]): Promise<void> {
    try {
      // Save each work order to offline storage
      // The workOrders from the list endpoint have a different structure than detailed work orders
      const promises = workOrders.map(async (workOrder) => {
        if (workOrder.workOrderID) {
          // Wrap the work order in the expected structure for offline storage
          const workOrderWrapper = {
            workOrder: workOrder
          }
          await workOrderOfflineStorage.saveWorkOrderOffline(
            workOrder.workOrderID,
            workOrderWrapper,
            true // fromServer = true
          )
        }
      })
      
      await Promise.all(promises)
    } catch (error) {
    }
  }
  
  /**
   * Load day destinations with offline-first strategy for today's date only
   * Priority: Server > Offline cache (today only) > Empty array
   */
  async loadDayDestinations(technicianId: number, date: string): Promise<TechnicianDayDestination[]> {
    const isToday = date === new Date().toISOString().split('T')[0]
    
    // 1. If online, try to fetch from server first
    if (navigator.onLine) {
      try {
        const serverDestinations = await workOrderService.getDayDestinations(technicianId, date)
        
        // Update offline cache with server data only for today
        if (isToday) {
          await workOrderOfflineStorage.saveDayDestinationsOffline(technicianId, date, serverDestinations)
        }
        
        return serverDestinations
      } catch (error) {
        // Continue to offline fallback only if it's today's date
      }
    }
    
    // 2. Only use offline data if it's today's date
    if (isToday) {
      const offlineDestinations = await workOrderOfflineStorage.getDayDestinationsOffline(technicianId, date)
      return offlineDestinations
    }
    
    // 3. Return empty array for non-today dates when offline or server fails
    return []
  }

  /**
   * Get current state of work orders list
   */
  async getWorkOrdersListState(technicianId: number, date: string, statusFilter: string): Promise<WorkOrdersListState> {
    const offlineWorkOrders = await workOrderOfflineStorage.getFilteredWorkOrdersOffline(
      technicianId, 
      date, 
      statusFilter
    )
    
    return {
      exists: offlineWorkOrders.length > 0,
      isOffline: !navigator.onLine,
      lastModified: Date.now(),
      count: offlineWorkOrders.length
    }
  }
}

export const offlineWorkOrdersListService = new OfflineWorkOrdersListService()